
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getPostById, updatePost } from '@/services/postService';
import { Layout } from '@/components/layout/Layout';
import { SEO } from '@/components/seo/SEO';
import { BoardWriteHeader } from '@/components/board/BoardWriteHeader';
import { BoardCategory } from '@/types/board';
import { BoardWriteForm } from '@/components/board/BoardWriteForm';

const EditPost = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [initialValues, setInitialValues] = useState<{
    title: string;
    content: string;
    category: BoardCategory;
  } | null>(null);

  const { data: post, isLoading: isLoadingPost } = useQuery({
    queryKey: ['post', id],
    queryFn: () => getPostById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    // Redirect if not authenticated
    if (!loading && !user) {
      toast({
        variant: "destructive",
        title: "로그인이 필요합니다",
        description: "게시글을 수정하려면 로그인해주세요."
      });
      navigate('/login', { replace: true });
      return;
    }

    // Set initial form values from post data
    if (post) {
      // Check if the current user is the author
      if (user && post.author_id !== user.id) {
        toast({
          variant: "destructive",
          title: "권한이 없습니다",
          description: "자신이 작성한 게시글만 수정할 수 있습니다."
        });
        navigate(`/board/post/${id}`, { replace: true });
        return;
      }

      setInitialValues({
        title: post.title,
        content: post.content,
        category: post.category as BoardCategory,
      });
    }
  }, [post, user, loading, navigate, toast, id]);

  const updatePostMutation = useMutation({
    mutationFn: (data: { title: string; content: string; category: BoardCategory }) => 
      updatePost(id!, data.title, data.content, data.category),
    onSuccess: () => {
      toast({
        title: "게시글이 수정되었습니다",
      });
      queryClient.invalidateQueries({ queryKey: ['post', id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      navigate(`/board/post/${id}`);
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "게시글 수정 실패",
        description: "오류가 발생했습니다. 다시 시도해주세요."
      });
    }
  });

  if (isLoadingPost || loading) {
    return (
      <Layout>
        <main className="container mx-auto py-8 px-4 flex items-center justify-center">
          <p>로딩 중...</p>
        </main>
      </Layout>
    );
  }

  if (!initialValues) {
    return (
      <Layout>
        <main className="container mx-auto py-8 px-4 flex items-center justify-center">
          <p>게시글을 불러오는 중...</p>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="게시글 수정" description="게시글을 수정합니다" />
      <main className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <BoardWriteHeader title="게시글 수정" />
          <BoardWriteForm 
            initialCategory={initialValues.category}
            initialTitle={initialValues.title}
            initialContent={initialValues.content}
            user={user}
            onSubmit={(title, content, category) => 
              updatePostMutation.mutate({ title, content, category })
            }
            isEdit={true}
          />
        </div>
      </main>
    </Layout>
  );
};

export default EditPost;
